import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { MapboxFeature } from '../types';
import mapboxgl from 'mapbox-gl';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiY292ZXJ6b25lIiwiYSI6ImNtMG03eDYxODA2YTYya3NheW4wM25nbjcifQ.zZ6gyVv7yu7kF-gmWRL-Qw';

const API_URL = process.env.REACT_APP_API_URL || '';

// Options arrays (matching the modal exactly)
const perimeterTypeOptions = ['Walls', 'Palisades', 'Electric Fencing', 'Barbed Wire'];
const wallMaterialsOptions = ['Brick', 'Cement', 'Corrugated Iron', 'Glass', 'Prefab', 'Asbestos', 'Wood', 'Other'];
const roofMaterialsOptions = ['Concrete', 'Corrugated Iron', 'Glass', 'Thatch', 'Asbestos', 'Wood', 'Tiles'];
const floorMaterialsOptions = ['Carpet', 'Sealed', 'Tiles', 'Wooden Floors'];
const surroundingAreaOptions = [
  'Farm', 'Plot', 'Golf Estate', 'Country Estate / Residential Area', 'Security Complex',
  'Complex With No Access Control', 'Boomed Off Suburb', 'Suburb', 'Industrial Area',
  'Commercial Area', 'Inner City / CBD', 'Informal Settlement', 'Retirement Village',
  'Nature Reserve', 'Holiday Resort', 'School/University', 'Parks', 'Highways',
  'Railway Lines', 'Shops/Cafes', 'Squatter Camps', 'Open Fields', 'Other'
];

// Currency formatting helpers
const formatCurrencyInput = (value: string): string => {
  if (!value) return '';
  const numericValue = value.replace(/[^\d.]/g, '');
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  if (decimalPart.length > 2) decimalPart = decimalPart.substring(0, 2);
  if (wholePart) wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
};

const formatCurrencyForDisplay = (value: number | string): string => {
  if (!value) return '';
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(numValue)) return value.toString();
  return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const convertToArray = (value: string | string[] | undefined): string[] => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const mapAddressDataForHousehold = (addressData: any) => {
  return {
    addressLine1: addressData.street || '',
    addressLine2: addressData.address_line_2 || '',
    suburb: addressData.state || '',
    cityTown: addressData.city || '',
    postalCode: addressData.zipCode || '',
    region: addressData.province || '',
    country: addressData.country || 'South Africa'
  };
};

const AddHouseholdContentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPolicy = searchParams.get('fromPolicy') === 'true';
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  const policyId = new URLSearchParams(location.search).get('policyId'); // ✅ ADD THIS
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddHouseholdContentsPage mounted:', { 
    quoteId, 
    clientId, 
    returnTo, 
    isMasterQuoteMode,
    policyId, // ✅ ADD THIS
    fromPolicy // ✅ ADD THIS
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);

  const [formData, setFormData] = useState({
    yearsLivedOnProperty: '',
    burglarBarType: '',
    dayTimeOccupantType: '',
    coverType: '',
    insuredValue: '',
    conductsBusiness: false,
    hasSurveillanceCamera: false,
    hasExternalSlidingDoor: false,
    hasSecurityGate: false,
    inSecurityComplex: false,
    hasAlarmRecords: false,
    hasNeighborhoodWatch: false,
    hasAccidentalDamageCover: false,
    accidentalDamageValue: '',
    hasPowerSurgeCover: false,
    powerSurgeValue: '',
    alarmSystemType: '',
    perimeterType: [] as string[],
    addressLine1: '',
    addressLine2: '',
    suburb: '',
    cityTown: '',
    postalCode: '',
    region: '',
    country: 'South Africa',
    propertyType: '',
    residenceType: '',
    buildingAge: '',
    situatedType: '',
    hasSwimmingPool: false,
    hasMotorizedGate: false,
    isPropertyUnoccupied: false,
    isCommune: false,
    underConstruction: false,
    builtOnSteepSlope: false,
    hasRetainingWalls: false,
    nearWaterBody: false,
    hasRisingDamp: false,
    hasWallCracks: false,
    historyOfLandslip: false,
    hasOutbuilding: false,
    hasPerimeterFence: false,
    hasElectricFence: false,
    hasThatchRoof: false,
    wallMaterials: [] as string[],
    roofMaterials: [] as string[],
    floorMaterials: [] as string[],
    otherSurroundingArea: [] as string[],
    premium: '',
    excess: '',
    noClaimBonus: '',
  });

  const [currencyDisplay, setCurrencyDisplay] = useState({
    insuredValue: '',
    premium: '',
    excess: '',
    accidentalDamageValue: '',
    powerSurgeValue: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [addressSuggestions, setAddressSuggestions] = useState<MapboxFeature[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch existing household contents if editing
useEffect(() => {
  const editId = new URLSearchParams(location.search).get('editId');
  if (editId && quoteId) {
    setEditingId(editId);
    fetchHouseholdForEdit(editId);
  }
}, [quoteId, location.search, policyId, fromPolicy]); // ✅ Add policyId and fromPolicy

  // Add this after the fetchUser useEffect
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    // Use a small delay to allow the suggestion click to register first
    setTimeout(() => {
      if (autocompleteInputRef.current && !autocompleteInputRef.current.contains(event.target as Node)) {
        setShowAddressSuggestions(false);
      }
    }, 200);
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

  // Helper function to convert comma-separated string or JSON string to array
const toArray = (value: any): string[] => {
  console.log('🔍 toArray input:', value, 'type:', typeof value);
  
  if (!value) return [];
  if (Array.isArray(value)) return value;
  
  if (typeof value === 'string') {
    let cleanValue = value.trim();
    
    // 1. Strip outer quotes if they exist
    if (cleanValue.startsWith('"') && cleanValue.endsWith('"')) {
      cleanValue = cleanValue.slice(1, -1);
    }
    if (cleanValue.startsWith("'") && cleanValue.endsWith("'")) {
      cleanValue = cleanValue.slice(1, -1);
    }
    
    // 2. Helper to strip JSON artifacts from individual items
    const cleanItem = (s: any) => String(s).replace(/^[{\["\s]+/, '').replace(/[}\]"\s]+$/, '').trim();

    // 3. Try JSON parse FIRST (handles ["Brick","Glass"])
    if (cleanValue.startsWith('[') && cleanValue.endsWith(']')) {
      try {
        const parsed = JSON.parse(cleanValue);
        if (Array.isArray(parsed)) {
          return parsed.map(cleanItem).filter(Boolean);
        }
      } catch (e) {
        // Fall through to comma-split if JSON is invalid
      }
    }
    
    // 4. Check for PostgreSQL array format {Brick,Glass}
    if (cleanValue.startsWith('{') && cleanValue.endsWith('}')) {
      return cleanValue.slice(1, -1).split(',').map(cleanItem).filter(Boolean);
    }
    
    // 5. If it's a plain comma-separated string (Brick,Glass)
    if (cleanValue.includes(',')) {
      return cleanValue.split(',').map(cleanItem).filter(Boolean);
    }
    
    // 6. Single value
    return cleanValue ? [cleanItem(cleanValue)] : [];
  }
  
  return [];
};

// Add these functions RIGHT AFTER the toArray function (before fetchHouseholdForEdit)

// Address autocomplete function
// Address autocomplete function with debouncing
const handleAddressSearch = async (searchText: string) => {
  if (!searchText || searchText.length < 3) {
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    return;
  }

  // Clear previous timer
  if (debounceTimerRef.current) {
    clearTimeout(debounceTimerRef.current);
  }

  // Set new timer
  debounceTimerRef.current = setTimeout(async () => {
    const token = 'pk.eyJ1IjoiY292ZXJ6b25lIiwiYSI6ImNtMG03eDYxODA2YTYya3NheW4wM25nbjcifQ.zZ6gyVv7yu7kF-gmWRL-Qw';
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchText)}.json?access_token=${token}&country=za&types=address&limit=5`;

    try {
      console.log('🔍 Fetching Mapbox suggestions for:', searchText);
      const response = await fetch(url);
      
      if (!response.ok) {
        console.error('Mapbox API error:', response.status, response.statusText);
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        return;
      }
      
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        setAddressSuggestions(data.features);
        setShowAddressSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  }, 500); // Wait 500ms after user stops typing
};

// Handle address selection from Mapbox suggestions (EXACT copy from AddressTab)
const handleAddressSelect = (feature: MapboxFeature) => {
  console.log('📍 Selected address feature:', feature);
  
  let street = feature.place_name.split(',')[0].trim();
  let city = '';
  let state = '';    // Suburb
  let province = ''; // Province
  let zipCode = '';

  // Debug: Log all context items with their types
  console.log('🔍 Mapbox context analysis:');
  feature.context?.forEach((ctx, index) => {
    console.log(`  [${index}] ${ctx.id} = "${ctx.text}"`);
  });

  // Extract address components from Mapbox context more specifically
  feature.context?.forEach((ctx) => {
    const ctxId = ctx.id;
    
    if (ctxId.startsWith('place.')) {
      city = ctx.text;
      console.log('🏙️ City found:', city);
    }
    else if (ctxId.startsWith('locality.') || ctxId.startsWith('neighborhood.')) {
      // Suburb is often in locality or neighborhood for South African addresses
      if (!state) state = ctx.text; // Only set if we don't have suburb yet
      console.log('🏘️ Suburb found:', ctx.text);
    }
    else if (ctxId.startsWith('region.')) {
      // Region is the province - this should be the only one
      province = ctx.text;
      console.log('🏞️ Province found:', province);
    }
    else if (ctxId.startsWith('postcode.')) {
      zipCode = ctx.text;
      console.log('📮 Postal code found:', zipCode);
    }
    else {
      console.log('⚡ Other context item:', ctxId, '=', ctx.text);
    }
  });

  // If we didn't find suburb, try to extract it from place_name
  if (!state) {
    const placeParts = feature.place_name.split(',');
    if (placeParts.length >= 2) {
      const potentialSuburb = placeParts[1]?.trim();
      if (potentialSuburb && potentialSuburb !== city && !potentialSuburb.match(/^\d+$/)) {
        state = potentialSuburb;
        console.log('🏘️ Suburb extracted from place_name:', state);
      }
    }
  }

  // Clean up province - remove any numbers (postal codes)
  if (province) {
    province = province.replace(/\d+/g, '').trim();
    console.log('🧹 Cleaned province:', province);
  }

  // If province is a short code, map it to full name
  if (province && province.length <= 3) {
    const provinceMap: { [key: string]: string } = {
      'KZN': 'KwaZulu-Natal',
      'NL': 'KwaZulu-Natal',
      'EC': 'Eastern Cape', 
      'WC': 'Western Cape',
      'NC': 'Northern Cape',
      'FS': 'Free State',
      'GT': 'Gauteng',
      'LP': 'Limpopo',
      'MP': 'Mpumalanga',
      'NW': 'North West'
    };
    const mappedProvince = provinceMap[province.toUpperCase()];
    if (mappedProvince) {
      province = mappedProvince;
      console.log('🗺️ Mapped province:', mappedProvince);
    }
  }

  console.log('📋 Final extracted values:', {
    street,
    city, 
    state,
    province,
    zipCode
  });

  // Update form data with extracted values
  setFormData(prev => ({
    ...prev,
    addressLine1: street,
    addressLine2: '',
    suburb: state,
    cityTown: city,
    region: province,
    postalCode: zipCode,
    country: 'South Africa'
  }));

  setAddressSuggestions([]);
  setShowAddressSuggestions(false);
};

  const fetchHouseholdForEdit = async (id: string) => {
  // Validate that id is a number
  if (!id || isNaN(parseInt(id))) {
    console.error('❌ Invalid edit ID:', id);
    setSaveMessage('Invalid ID for editing');
    return;
  }
  
  try {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/api/household-contents/${id}`;
    console.log('🔍 Fetching household contents from URL:', url);
    
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    // Handle both wrapped and unwrapped responses
    let data = response.data;
    if (data.data) {
      data = data.data;
    }
    
    console.log('✅ Fetched household data:', data);
    // ✅ DEBUG: Log the specific fields we care about
    console.log('🔍 DATA MAPPING CHECK:', {
      insuredValue: data.insuredValue,
      insured_value: data.insured_value,
      typeofInsuredValue: typeof data.insuredValue,
      premium: data.premium,
      excess: data.excess,
    });
    
    // Map snake_case from backend to camelCase for frontend
    setFormData({
      yearsLivedOnProperty: data.yearsLivedOnProperty?.toString() || '',
      burglarBarType: data.burglarBarType || '',
      dayTimeOccupantType: data.dayTimeOccupantType || '',
      coverType: data.coverType || '',
      insuredValue: data.insuredValue?.toString() || '',
      conductsBusiness: data.conductsBusiness || false,
      hasSurveillanceCamera: data.hasSurveillanceCamera || false,
      hasExternalSlidingDoor: data.hasExternalSlidingDoor || false,
      hasSecurityGate: data.hasSecurityGate || false,
      inSecurityComplex: data.inSecurityComplex || false,
      hasAlarmRecords: data.hasAlarmRecords || false,
      hasNeighborhoodWatch: data.hasNeighborhoodWatch || false,
      hasAccidentalDamageCover: data.hasAccidentalDamageCover || false,
      accidentalDamageValue: (data.accidentalDamageValue || 0).toString(),
      hasPowerSurgeCover: data.hasPowerSurgeCover || false,
      powerSurgeValue: (data.powerSurgeValue || 0).toString(),
      alarmSystemType: data.alarmSystemType || '',
      perimeterType: toArray(data.perimeter_type || data.perimeterType),
      addressLine1: data.addressLine1 || '',
      addressLine2: data.addressLine2 || '',
      suburb: data.suburb || '',
      cityTown: data.cityTown || '',
      postalCode: data.postalCode || '',
      region: data.region || '',
      country: data.country || 'South Africa',
      propertyType: data.propertyType || '',
      residenceType: data.residenceType || '',
      buildingAge: data.buildingAge || '',
      situatedType: data.situatedType || '',
      hasSwimmingPool: data.hasSwimmingPool || false,
      hasMotorizedGate: data.hasMotorizedGate || false,
      isPropertyUnoccupied: data.isPropertyUnoccupied || false,
      isCommune: data.isCommune || false,
      underConstruction: data.underConstruction || false,
      builtOnSteepSlope: data.builtOnSteepSlope || false,
      hasRetainingWalls: data.hasRetainingWalls || false,
      nearWaterBody: data.nearWaterBody || false,
      hasRisingDamp: data.hasRisingDamp || false,
      hasWallCracks: data.hasWallCracks || false,
      historyOfLandslip: data.historyOfLandslip || false,
      hasOutbuilding: data.hasOutbuilding || false,
      hasPerimeterFence: data.hasPerimeterFence || false,
      hasElectricFence: data.hasElectricFence || false,
      hasThatchRoof: data.hasThatchRoof || false,
      wallMaterials: toArray(data.wall_materials || data.wallMaterials),
      roofMaterials: toArray(data.roof_materials || data.roofMaterials),
      floorMaterials: toArray(data.floor_materials || data.floorMaterials),
      otherSurroundingArea: toArray(data.other_surrounding_area || data.otherSurroundingArea),
      premium: data.premium?.toString() || '',
      excess: data.excess?.toString() || '',
      noClaimBonus: data.noClaimBonus || '',
    });
    
    setCurrencyDisplay({
      insuredValue: formatCurrencyInput(data.insuredValue?.toString() || ''),
      premium: formatCurrencyInput(data.premium?.toString() || ''),
      excess: formatCurrencyInput(data.excess?.toString() || ''),
      accidentalDamageValue: formatCurrencyInput((data.accidentalDamageValue || 0).toString()),
      powerSurgeValue: formatCurrencyInput((data.powerSurgeValue || 0).toString()),
    });
    
  } catch (error: any) {
    console.error('❌ Error fetching household contents for edit:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url
    });
    setSaveMessage(`Failed to load: ${error.response?.data?.message || error.message || 'Unknown error'}`);
  }
};

  const fetchClientAddress = async () => {
    setIsFetchingAddress(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/addresses/client/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const addressData = await response.json();
        const mappedData = mapAddressDataForHousehold(addressData);
        setFormData(prev => ({ ...prev, ...mappedData }));
        setSaveMessage('✓ Address filled successfully from client data!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      setSaveMessage('❌ Failed to fetch address');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsFetchingAddress(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const rawValue = e.target.value;
    let cleanValue = rawValue.replace(/[^\d.,]/g, '');
    const decimalCount = (cleanValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = cleanValue.indexOf('.');
      cleanValue = cleanValue.substring(0, firstDecimalIndex + 1) + 
                   cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
    }
    if (cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      if (parts[1].length > 2) cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
    }
    const formattedValue = formatCurrencyInput(cleanValue);
    setCurrencyDisplay(prev => ({ ...prev, [fieldName]: formattedValue }));
    const numericValue = parseCurrencyInput(cleanValue);
    setFormData(prev => ({ ...prev, [fieldName]: numericValue.toString() }));
    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleCheckboxGroupChange = (field: string, option: string, isChecked: boolean) => {
    setFormData(prev => {
      const currentArray = (prev[field as keyof typeof prev] as string[]) || [];
      if (isChecked && !currentArray.includes(option)) {
        return { ...prev, [field]: [...currentArray, option] };
      } else if (!isChecked && currentArray.includes(option)) {
        return { ...prev, [field]: currentArray.filter(item => item !== option) };
      }
      return prev;
    });
  };

  const clearAllSelections = (field: string) => {
    setFormData(prev => ({ ...prev, [field]: [] }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.addressLine1) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.suburb) newErrors.suburb = 'Suburb is required';
    if (!formData.cityTown) newErrors.cityTown = 'City/Town is required';
    if (!formData.coverType) newErrors.coverType = 'Cover type is required';
    if (!formData.insuredValue || parseFloat(formData.insuredValue) <= 0) newErrors.insuredValue = 'Insured value must be a positive number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isSubmitting) return;
  if (!validateForm()) return;
  
  // Validate clientId
  if (!clientId || isNaN(parseInt(clientId))) {
    setErrors(prev => ({ ...prev, server: 'Valid client ID is required' }));
    return;
  }
  
  setIsSubmitting(true);
  setSaveMessage('Saving household contents...');

  // Clean the quoteId - convert "null" string or invalid values to actual null
  let cleanQuoteId: number | null = null;
  if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
    cleanQuoteId = parseInt(quoteId);
  }

  const submitData = {
    // Use camelCase to match backend expectations
    addressLine1: formData.addressLine1,
    addressLine2: formData.addressLine2,
    suburb: formData.suburb,
    cityTown: formData.cityTown,
    postalCode: formData.postalCode,
    region: formData.region,
    country: formData.country,
    propertyType: formData.propertyType,
    residenceType: formData.residenceType,
    buildingAge: formData.buildingAge,
    situatedType: formData.situatedType,
    coverType: formData.coverType,
    insuredValue: parseFloat(formData.insuredValue) || 0,
    yearsLivedOnProperty: formData.yearsLivedOnProperty ? parseInt(formData.yearsLivedOnProperty) : null,
    burglarBarType: formData.burglarBarType || null,
    dayTimeOccupantType: formData.dayTimeOccupantType || null,
    conductsBusiness: formData.conductsBusiness,
    hasSurveillanceCamera: formData.hasSurveillanceCamera,
    hasExternalSlidingDoor: formData.hasExternalSlidingDoor,
    hasSecurityGate: formData.hasSecurityGate,
    inSecurityComplex: formData.inSecurityComplex,
    hasAlarmRecords: formData.hasAlarmRecords,
    hasNeighborhoodWatch: formData.hasNeighborhoodWatch,
    alarmSystemType: formData.alarmSystemType || null,
    perimeterType: Array.isArray(formData.perimeterType) ? formData.perimeterType : formData.perimeterType,
    hasAccidentalDamageCover: formData.hasAccidentalDamageCover,
    accidentalDamageValue: parseFloat(formData.accidentalDamageValue) || 0,
    hasPowerSurgeCover: formData.hasPowerSurgeCover,
    powerSurgeValue: parseFloat(formData.powerSurgeValue) || 0,
    hasSwimmingPool: formData.hasSwimmingPool,
    hasMotorizedGate: formData.hasMotorizedGate,
    isPropertyUnoccupied: formData.isPropertyUnoccupied,
    isCommune: formData.isCommune,
    underConstruction: formData.underConstruction,
    builtOnSteepSlope: formData.builtOnSteepSlope,
    hasRetainingWalls: formData.hasRetainingWalls,
    nearWaterBody: formData.nearWaterBody,
    hasRisingDamp: formData.hasRisingDamp,
    hasWallCracks: formData.hasWallCracks,
    historyOfLandslip: formData.historyOfLandslip,
    hasOutbuilding: formData.hasOutbuilding,
    hasPerimeterFence: formData.hasPerimeterFence,
    hasElectricFence: formData.hasElectricFence,
    hasThatchRoof: formData.hasThatchRoof,
    wallMaterials: formData.wallMaterials,
    roofMaterials: formData.roofMaterials,
    floorMaterials: formData.floorMaterials,
    otherSurroundingArea: formData.otherSurroundingArea,
    premium: parseFloat(formData.premium) || 0,
    excess: parseFloat(formData.excess) || 0,
    noClaimBonus: formData.noClaimBonus || null,
    quote_id: cleanQuoteId,
    client_id: parseInt(clientId)
  };

  try {
    const token = localStorage.getItem('token');
    
    // If no quote_id, save to localStorage (draft mode)
    if (!cleanQuoteId) {
      console.log('💾 No quote ID - saving household contents to localStorage');
      
      // ✅ Determine the storage key based on context
      let storageKey: string;
      
      // ✅ Only use policy key if we're explicitly coming from policy
      if (policyId && fromPolicy === true) {
        // Coming from Policy Detail page - save with policy ID key
        storageKey = `policy_${policyId}_household`;
        console.log(`📂 Saving household contents with policy key: ${storageKey}`);
      } else {
        // Coming from Quote/Create Quote page - save with client draft key
        storageKey = `client_${clientId}_household_contents_draft`;
        console.log(`📂 Saving household contents with client draft key: ${storageKey}`);
      }
      
      const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Check if we're editing an existing draft
      let updatedDrafts;
      if (editingId && editingId.startsWith('temp-')) {
        // Update existing draft
        updatedDrafts = existingDrafts.map((item: any) => 
          item.id === editingId 
            ? { 
                ...item, 
                ...submitData, 
                id: editingId,
                isTemp: true,
                status: 'active',
                inception_date: new Date().toISOString().split('T')[0],
                product_type: 'household-contents'
              }
            : item
        );
      } else {
        // Add new draft
        updatedDrafts = [
          ...existingDrafts,
          {
            ...submitData,
            id: `temp-${Date.now()}`,
            isTemp: true,
            status: 'active',
            inception_date: new Date().toISOString().split('T')[0],
            product_type: 'household-contents'
          }
        ];
      }
      
      localStorage.setItem(storageKey, JSON.stringify(updatedDrafts));
      
      setSaveMessage('Household contents saved to draft!');
      
      // ✅ Navigate based on context
      setTimeout(() => {
        if (policyId && fromPolicy) {
          // Return to policy detail page
          const returnUrl = returnTo || `/policy/${policyId}`;
          const separator = returnUrl.includes('?') ? '&' : '?';
          const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=household&clientId=${clientId}`;
          console.log('📍 Returning to policy detail page:', finalUrl);
          navigate(finalUrl);
        } else if (isMasterQuoteMode && returnTo) {
          // Master quote mode - navigate back to master quote page
          const decodedReturnUrl = decodeURIComponent(returnTo);
          const separator = decodedReturnUrl.includes('?') ? '&' : '?';
          const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=household&clientId=${clientId}`;
          console.log('📍 Returning to master quote page:', finalUrl);
          navigate(finalUrl);
        } else {
          // Create quote mode
          navigate(`/create-quote/${clientId}`, { 
            state: { refreshProducts: true, hasDrafts: true }
          });
        }
      }, 1500);

    } else {
      // Has quote_id - save to database
      if (editingId) {
        await axios.put(`${API_URL}/api/household-contents/${editingId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSaveMessage('Household contents updated successfully!');
        
        // ✅ LOG ACTIVITY: Household Contents Updated
        if (cleanQuoteId) {
          try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const performedBy = user?.username || user?.email || 'System';
            
            await axios.post(
              `${API_URL}/api/quote-activity-logs`,
              {
                quote_id: cleanQuoteId,
                action_type: 'product_updated',
                description: `Household contents updated: ${formData.propertyType || 'Household'}`,
                metadata: {
                  productType: 'household',
                  productId: editingId,
                  performedBy: performedBy,
                  address: `${formData.addressLine1 || ''} ${formData.suburb || ''}`.trim() || 'No address',
                  propertyType: formData.propertyType || 'Not specified',
                  insuredValue: parseFloat(formData.insuredValue) || 0,
                  coverType: formData.coverType || '',
                  wallMaterials: formData.wallMaterials || [],
                  roofMaterials: formData.roofMaterials || [],
                  floorMaterials: formData.floorMaterials || [],
                  otherSurroundingArea: formData.otherSurroundingArea || [],
                  updatedAt: new Date().toISOString()
                }
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('✅ Activity logged: household_updated');
          } catch (logError) {
            console.error('❌ Failed to log household update:', logError);
            // Don't fail the update if logging fails
          }
        }
      } else {
        await axios.post(`${API_URL}/api/household-contents`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSaveMessage('Household contents added successfully!');
        
        // ✅ LOG ACTIVITY: Household Contents Added
        if (cleanQuoteId) {
          try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const performedBy = user?.username || user?.email || 'System';
            
            await axios.post(
              `${API_URL}/api/quote-activity-logs`,
              {
                quote_id: cleanQuoteId,
                action_type: 'product_added',
                description: `Household contents added: ${formData.propertyType || 'Household'}`,
                metadata: {
                  productType: 'household',
                  performedBy: performedBy,
                  address: `${formData.addressLine1 || ''} ${formData.suburb || ''}`.trim() || 'No address',
                  propertyType: formData.propertyType || 'Not specified',
                  insuredValue: parseFloat(formData.insuredValue) || 0,
                  coverType: formData.coverType || '',
                  wallMaterials: formData.wallMaterials || [],
                  roofMaterials: formData.roofMaterials || [],
                  floorMaterials: formData.floorMaterials || [],
                  otherSurroundingArea: formData.otherSurroundingArea || [],
                  addedAt: new Date().toISOString()
                }
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('✅ Activity logged: household_added');
          } catch (logError) {
            console.error('❌ Failed to log household addition:', logError);
            // Don't fail the addition if logging fails
          }
        }
      }
      
      // ✅ FIX: Navigate based on mode
      const validClientId = clientId || new URLSearchParams(location.search).get('clientId');
      
      if (!validClientId) {
        console.error('❌ No clientId available for navigation');
        setSaveMessage('Error: Missing client ID');
        setIsSubmitting(false);
        return;
      }
      
      let targetUrl = '';
      
      if (isMasterQuoteMode && returnTo) {
        // Master quote mode - navigate back to master quote page
        const decodedReturnUrl = decodeURIComponent(returnTo);
        const separator = decodedReturnUrl.includes('?') ? '&' : '?';
        targetUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=household&clientId=${validClientId}`;
        console.log('📍 Returning to master quote page:', targetUrl);
      } else if (cleanQuoteId) {
        // Create quote mode but with quoteId - navigate to create quote page
        targetUrl = `/create-quote/${validClientId}?refreshProducts=true&activeTab=quotes`;
        console.log('📍 Returning to create quote page:', targetUrl);
      } else {
        // Fallback
        targetUrl = `/create-quote/${validClientId}?refreshProducts=true`;
        console.log('📍 Fallback navigation:', targetUrl);
      }
      
      // ✅ Use both navigate and window.location for reliability
      setTimeout(() => {
        // Try React Router navigation first
        navigate(targetUrl);
        
        // If the page doesn't navigate within 500ms, force a hard navigation
        setTimeout(() => {
          // Check if we're still on the same page by checking if the URL changed
          const currentPath = window.location.pathname;
          if (currentPath.includes('/add-household-contents/')) {
            console.log('⚠️ React Router navigation didn\'t work, using window.location');
            window.location.href = targetUrl;
          }
        }, 500);
      }, 1500);
    }
      
  } catch (error: any) {
    console.error('Error saving household contents:', error);
    setSaveMessage(error.response?.data?.message || 'Failed to save household contents');
    setIsSubmitting(false);
  }
};

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Household Contents' : 'Add Household Contents'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">

            <button
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=household&clientId=${clientId}`;
      console.log('📍 Back button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=household&clientId=${clientId}`;
      console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      // Go back to create quote page
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
>
  ← {fromPolicy ? 'Back to Policy Details' : 'Back to Quote'}
</button>
            
            <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingId ? 'Edit Household Contents' : 'Add Household Contents'}
              </h2>

              {saveMessage && (
                <div className={`${saveMessage.includes('❌') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} border px-4 py-3 rounded relative mb-4`}>
                  {saveMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Years Lived, Burglar Bar Type, Day Time Occupant */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Years Lived on Property</label>
                      <input
                        name="yearsLivedOnProperty"
                        type="number"
                        value={formData.yearsLivedOnProperty}
                        onChange={handleInputChange}
                        className="border border-gray-300 rounded-md p-2 w-full"
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                      {errors.yearsLivedOnProperty && <p className="text-red-500 text-sm">{errors.yearsLivedOnProperty}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Burglar Bar Type</label>
                      <select
                        name="burglarBarType"
                        value={formData.burglarBarType}
                        onChange={handleInputChange}
                        className="border border-gray-300 rounded-md p-2 w-full"
                      >
                        <option value="">Select burglar bar type</option>
                        <option value="None">None</option>
                        <option value="All Windows">All Windows</option>
                        <option value="Opening Windows Only">Opening Windows Only</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Day Time Occupant Type</label>
                      <select
                        name="dayTimeOccupantType"
                        value={formData.dayTimeOccupantType}
                        onChange={handleInputChange}
                        className="border border-gray-300 rounded-md p-2 w-full"
                      >
                        <option value="">Select occupant type</option>
                        <option value="none">None</option>
                        <option value="household_member">Household Member</option>
                        <option value="domestic_employee">Domestic Employee</option>
                        <option value="tenant">Tenant</option>
                        <option value="insured">Insured</option>
                        <option value="outstanding">Outstanding</option>
                        <option value="family_member">Family Member</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Cover Type, Insured Value, No Claim Bonus */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Cover Type</label>
                      <select
                        name="coverType"
                        value={formData.coverType}
                        onChange={handleInputChange}
                        className="border border-gray-300 rounded-md p-2 w-full"
                      >
                        <option value="">Select cover type</option>
                        <option value="Comprehensive">Comprehensive</option>
                        <option value="Theft Cover Excluding">Theft Cover Excluding</option>
                        <option value="Flood Cover Excluding">Flood Cover Excluding</option>
                        <option value="Advensure">Advensure</option>
                      </select>
                      {errors.coverType && <p className="text-red-500 text-sm">{errors.coverType}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Insured Value</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R</span>
                        <input
                          type="text"
                          value={currencyDisplay.insuredValue}
                          onChange={(e) => handleCurrencyChange(e, 'insuredValue')}
                          className="border border-gray-300 rounded-md p-2 pl-8 w-full"
                          placeholder="0.00"
                        />
                      </div>
                      {errors.insuredValue && <p className="text-red-500 text-sm">{errors.insuredValue}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">No Claim Bonus</label>
                      <select
                        name="noClaimBonus"
                        value={formData.noClaimBonus}
                        onChange={handleInputChange}
                        className="border border-gray-300 rounded-md p-2 w-full"
                      >
                        <option value="">Select no claim bonus</option>
                        <option value="12 Months  (1 Year)">12 Months (1 Year)</option>
                        <option value="24 Months  (2 Year)">24 Months (2 Year)</option>
                        <option value="36 Months  (3 Year)">36 Months (3 Year)</option>
                        <option value="48 Months  (4 Year)">48 Months (4 Year)</option>
                        <option value="60 Months  (5 Year)">60 Months (5 Year)</option>
                        <option value="72 Months  (6 Year)">72 Months (6 Year)</option>
                        <option value="84 Months  (7 Year)">84 Months (7 Year)</option>
                        <option value="96 Months  (8 Year)">96 Months (8 Year)</option>
                        <option value="108 Months  (9 Year)">108 Months (9 Year)</option>
                        <option value="120Months  (10 Year)">120 Months (10 Year)</option>
                      </select>
                    </div>
                  </div>

                  {/* Security Features Checkboxes */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h3 className="text-lg font-semibold mb-3">Security Features</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input type="checkbox" name="conductsBusiness" checked={formData.conductsBusiness} onChange={handleInputChange} className="mr-2" />
                          Does the resident conduct a business on the premises
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="hasSurveillanceCamera" checked={formData.hasSurveillanceCamera} onChange={handleInputChange} className="mr-2" />
                          Has surveillance camera
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="hasExternalSlidingDoor" checked={formData.hasExternalSlidingDoor} onChange={handleInputChange} className="mr-2" />
                          Has external sliding door
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="hasSecurityGate" checked={formData.hasSecurityGate} onChange={handleInputChange} className="mr-2" />
                          Has security gate on external doors
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="inSecurityComplex" checked={formData.inSecurityComplex} onChange={handleInputChange} className="mr-2" />
                          Is in a security complex
                        </label>
                      </div>
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input type="checkbox" name="hasAlarmRecords" checked={formData.hasAlarmRecords} onChange={handleInputChange} className="mr-2" />
                          Has 2 month alarm triggered records
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="hasNeighborhoodWatch" checked={formData.hasNeighborhoodWatch} onChange={handleInputChange} className="mr-2" />
                          Has an active neighbourhood watch
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" name="hasAccidentalDamageCover" checked={formData.hasAccidentalDamageCover} onChange={handleInputChange} className="mr-2" />
                          Accidental damage cover
                        </label>
                        {formData.hasAccidentalDamageCover && (
                          <div className="ml-6">
                            <label className="text-sm font-medium block mb-1">Accidental Damage Cover Sum Insured</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R</span>
                              <input
                                type="text"
                                value={currencyDisplay.accidentalDamageValue}
                                onChange={(e) => handleCurrencyChange(e, 'accidentalDamageValue')}
                                className="border border-gray-300 rounded-md p-2 pl-8 w-full"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        )}
                        <label className="flex items-center">
                          <input type="checkbox" name="hasPowerSurgeCover" checked={formData.hasPowerSurgeCover} onChange={handleInputChange} className="mr-2" />
                          Power surge cover
                        </label>
                        {formData.hasPowerSurgeCover && (
                          <div className="ml-6">
                            <label className="text-sm font-medium block mb-1">Power Surge Cover Sum Insured</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R</span>
                              <input
                                type="text"
                                value={currencyDisplay.powerSurgeValue}
                                onChange={(e) => handleCurrencyChange(e, 'powerSurgeValue')}
                                className="border border-gray-300 rounded-md p-2 pl-8 w-full"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Alarm System Type */}
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-200">
                    <div>
                      <label className="text-sm font-medium mb-1 block">What type of alarm system does the property have?</label>
                      <select
                        name="alarmSystemType"
                        value={formData.alarmSystemType}
                        onChange={handleInputChange}
                        className="border border-gray-300 rounded-md p-2 w-full"
                      >
                        <option value="">Select alarm system type</option>
                        <option value="none">None</option>
                        <option value="alarm">Alarm</option>
                        <option value="radio_linked_alarm">Radio Linked Alarm</option>
                        <option value="24_hour_armed_response">24 Hour Armed Response</option>
                        <option value="self_maintenance_alarm">Self Maintenance Alarm</option>
                      </select>
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={fetchClientAddress}
                        disabled={isFetchingAddress}
                        className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 disabled:bg-gray-300"
                      >
                        {isFetchingAddress ? 'Fetching...' : 'Fill from Client'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">

                      <div className="col-span-2 relative">
                        <label className="text-sm font-medium mb-1 block">Address Line 1</label>
                        <input
                          type="text"
                          name="addressLine1"
                          ref={autocompleteInputRef}
                          value={formData.addressLine1}
                          onChange={(e) => {
                            handleInputChange(e);
                            handleAddressSearch(e.target.value);
                          }}
                          onFocus={() => {
                            if (formData.addressLine1 && formData.addressLine1.length >= 3) {
                              handleAddressSearch(formData.addressLine1);
                            }
                          }}
                          className="border border-gray-300 rounded-md p-2 w-full"
                          placeholder="Start typing address..."
                          autoComplete="off"
                        />
                        {errors.addressLine1 && <p className="text-red-500 text-sm">{errors.addressLine1}</p>}
                        
                        {/* Address Suggestions Dropdown */}
                        {showAddressSuggestions && addressSuggestions.length > 0 && (
                          <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                            {addressSuggestions.map((suggestion) => (
                              <li
                                key={suggestion.id}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('🔍 Suggestion clicked:', suggestion);
                                  handleAddressSelect(suggestion);
                                }}
                              >
                                {suggestion.place_name}
                              </li>
                            ))}
                          </ul>
                        )}
                        {showAddressSuggestions && addressSuggestions.length === 0 && formData.addressLine1.length >= 3 && (
                          <p className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 px-4 py-2 text-sm text-gray-500">
                            No address suggestions found
                          </p>
                        )}
                      </div>

                      <div className="col-span-2">
                        <label className="text-sm font-medium mb-1 block">Address Line 2</label>
                        <input name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Suburb</label>
                        <input name="suburb" value={formData.suburb} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full" />
                        {errors.suburb && <p className="text-red-500 text-sm">{errors.suburb}</p>}
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">City/Town</label>
                        <input name="cityTown" value={formData.cityTown} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full" />
                        {errors.cityTown && <p className="text-red-500 text-sm">{errors.cityTown}</p>}
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Postal Code</label>
                        <input name="postalCode" value={formData.postalCode} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Region</label>
                        <input name="region" value={formData.region} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm font-medium mb-1 block">Country</label>
                        <input name="country" value={formData.country} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full" />
                      </div>
                    </div>
                  </div>

                  {/* Property Details Section */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h3 className="text-lg font-semibold mb-3">Property Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Property Type</label>
                        <select name="propertyType" value={formData.propertyType} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full">
                          <option value="">Select property type</option>
                          <option value="house">House</option><option value="duplex">Duplex</option><option value="townhouse">Townhouse</option>
                          <option value="apartment">Apartment</option><option value="condo">Condo</option><option value="villa">Villa</option>
                          <option value="cluster">Cluster</option><option value="cottage_attached">Cottage Attached</option>
                          <option value="cottage_not_attached">Cottage Not Attached</option><option value="flat_above_ground">Flat Above Ground</option>
                          <option value="flat_on_ground">Flat On Ground</option><option value="outbuilding">Outbuilding</option>
                          <option value="park_home">Park Home / Caravan</option><option value="plot">Plot</option>
                          <option value="established">Established</option><option value="new_extension">New Extension</option>
                          <option value="farm">Farm</option><option value="informal_settlement">Informal Settlement</option>
                          <option value="commercial">Commercial</option><option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Residence Type</label>
                        <select name="residenceType" value={formData.residenceType} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full">
                          <option value="">Select residence type</option>
                          <option value="primary">Primary Residence</option><option value="secondary">Secondary Residence</option>
                          <option value="holiday">Holiday Residence</option><option value="rental">Rental / Tenant Occupied</option>
                          <option value="vacant">Vacant / Unoccupied</option><option value="business">Business / Commercial</option>
                          <option value="mixed">Mixed Use</option><option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Building Age</label>
                        <select name="buildingAge" value={formData.buildingAge} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full">
                          <option value="">Select building age</option>
                          <option value="0-5">0-5 Years</option><option value="6-10">6-10 Years</option>
                          <option value="11-25">11-25 Years</option><option value="25+">Older Than 25 Years</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Situated Type</label>
                        <select name="situatedType" value={formData.situatedType} onChange={handleInputChange} className="border border-gray-300 rounded-md p-2 w-full">
                          <option value="">Select situated type</option>
                          <option value="urban">Urban</option><option value="suburban">Suburban</option><option value="rural">Rural</option>
                          <option value="coastal">Coastal</option><option value="mountain">Mountain</option><option value="island">Island</option>
                          <option value="industrial">Industrial</option><option value="commercial">Commercial</option>
                          <option value="mixed">Mixed Use</option><option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Property Features Checkboxes */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="text-md font-semibold mb-3">Property Features</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <label className="flex items-center"><input type="checkbox" name="hasSwimmingPool" checked={formData.hasSwimmingPool} onChange={handleInputChange} className="mr-2" /> Has swimming pool</label>
                        <label className="flex items-center"><input type="checkbox" name="hasMotorizedGate" checked={formData.hasMotorizedGate} onChange={handleInputChange} className="mr-2" /> Has motorized gate</label>
                        <label className="flex items-center"><input type="checkbox" name="isPropertyUnoccupied" checked={formData.isPropertyUnoccupied} onChange={handleInputChange} className="mr-2" /> Is property unoccupied for more than 60 consecutive days per annum</label>
                        <label className="flex items-center"><input type="checkbox" name="isCommune" checked={formData.isCommune} onChange={handleInputChange} className="mr-2" /> Is commune</label>
                        <label className="flex items-center"><input type="checkbox" name="underConstruction" checked={formData.underConstruction} onChange={handleInputChange} className="mr-2" /> Under construction</label>
                        <label className="flex items-center"><input type="checkbox" name="builtOnSteepSlope" checked={formData.builtOnSteepSlope} onChange={handleInputChange} className="mr-2" /> Built on steep slope</label>
                        <label className="flex items-center"><input type="checkbox" name="hasRetainingWalls" checked={formData.hasRetainingWalls} onChange={handleInputChange} className="mr-2" /> Has the property any excessive use of retaining walls</label>
                      </div>
                      <div className="space-y-3">
                        <label className="flex items-center"><input type="checkbox" name="nearWaterBody" checked={formData.nearWaterBody} onChange={handleInputChange} className="mr-2" /> Less than 100 m from Sea, Lagoon, River, Natural Stream, Dam or Swamp</label>
                        <label className="flex items-center"><input type="checkbox" name="hasRisingDamp" checked={formData.hasRisingDamp} onChange={handleInputChange} className="mr-2" /> Has the property any visible signs of rising damp</label>
                        <label className="flex items-center"><input type="checkbox" name="hasWallCracks" checked={formData.hasWallCracks} onChange={handleInputChange} className="mr-2" /> Has the property any visible signs of cracks in the wall</label>
                        <label className="flex items-center"><input type="checkbox" name="historyOfLandslip" checked={formData.historyOfLandslip} onChange={handleInputChange} className="mr-2" /> History of landslip or Subsidence in the area</label>
                        <label className="flex items-center"><input type="checkbox" name="hasOutbuilding" checked={formData.hasOutbuilding} onChange={handleInputChange} className="mr-2" /> Has outbuilding</label>
                        <label className="flex items-center"><input type="checkbox" name="hasPerimeterFence" checked={formData.hasPerimeterFence} onChange={handleInputChange} className="mr-2" /> Property surrounded by perimeter fence at least 1.8m high</label>
                        <label className="flex items-center"><input type="checkbox" name="hasElectricFence" checked={formData.hasElectricFence} onChange={handleInputChange} className="mr-2" /> Has electric fence on perimeter</label>
                        <label className="flex items-center"><input type="checkbox" name="hasThatchRoof" checked={formData.hasThatchRoof} onChange={handleInputChange} className="mr-2" /> Thatch roof present</label>
                      </div>
                    </div>
                  </div>

                  {/* Material Details Section */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="text-md font-semibold mb-3">Material Details</h4>
                    <div className="space-y-6">
                      {/* Perimeter Type */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Perimeter Type</label>
                        <div className="grid grid-cols-2 gap-2">
                          {perimeterTypeOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input type="checkbox" checked={formData.perimeterType.includes(option)} onChange={(e) => handleCheckboxGroupChange('perimeterType', option, e.target.checked)} className="mr-2" />
                              {option}
                            </label>
                          ))}
                        </div>
                        {formData.perimeterType.length > 0 && <p className="text-xs text-green-600 mt-1">Selected: {formData.perimeterType.join(', ')}</p>}
                        <button type="button" onClick={() => clearAllSelections('perimeterType')} className="text-xs text-red-500 hover:text-red-700 mt-1">Clear All</button>
                      </div>

                      {/* Wall Materials */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Wall Materials</label>
                        <div className="grid grid-cols-2 gap-2">
                          {wallMaterialsOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input type="checkbox" checked={formData.wallMaterials.includes(option)} onChange={(e) => handleCheckboxGroupChange('wallMaterials', option, e.target.checked)} className="mr-2" />
                              {option}
                            </label>
                          ))}
                        </div>
                        {formData.wallMaterials.length > 0 && <p className="text-xs text-green-600 mt-1">Selected: {formData.wallMaterials.join(', ')}</p>}
                        <button type="button" onClick={() => clearAllSelections('wallMaterials')} className="text-xs text-red-500 hover:text-red-700 mt-1">Clear All</button>
                      </div>

                      {/* Roof Materials */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Roof Materials</label>
                        <div className="grid grid-cols-2 gap-2">
                          {roofMaterialsOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input type="checkbox" checked={formData.roofMaterials.includes(option)} onChange={(e) => handleCheckboxGroupChange('roofMaterials', option, e.target.checked)} className="mr-2" />
                              {option}
                            </label>
                          ))}
                        </div>
                        {formData.roofMaterials.length > 0 && <p className="text-xs text-green-600 mt-1">Selected: {formData.roofMaterials.join(', ')}</p>}
                        <button type="button" onClick={() => clearAllSelections('roofMaterials')} className="text-xs text-red-500 hover:text-red-700 mt-1">Clear All</button>
                      </div>

                      {/* Floor Materials */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Floor Materials</label>
                        <div className="grid grid-cols-2 gap-2">
                          {floorMaterialsOptions.map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input type="checkbox" checked={formData.floorMaterials.includes(option)} onChange={(e) => handleCheckboxGroupChange('floorMaterials', option, e.target.checked)} className="mr-2" />
                              {option}
                            </label>
                          ))}
                        </div>
                        {formData.floorMaterials.length > 0 && <p className="text-xs text-green-600 mt-1">Selected: {formData.floorMaterials.join(', ')}</p>}
                        <button type="button" onClick={() => clearAllSelections('floorMaterials')} className="text-xs text-red-500 hover:text-red-700 mt-1">Clear All</button>
                      </div>

                      {/* Other Surrounding Area */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Other Surrounding Area</label>
                        <div className="grid grid-cols-2 gap-2">
                          {surroundingAreaOptions.slice(0, 8).map(option => (
                            <label key={option} className="flex items-center text-sm">
                              <input type="checkbox" checked={formData.otherSurroundingArea.includes(option)} onChange={(e) => handleCheckboxGroupChange('otherSurroundingArea', option, e.target.checked)} className="mr-2" />
                              {option}
                            </label>
                          ))}
                        </div>
                        {formData.otherSurroundingArea.length > 0 && <p className="text-xs text-green-600 mt-1">{formData.otherSurroundingArea.length} areas selected</p>}
                        <button type="button" onClick={() => clearAllSelections('otherSurroundingArea')} className="text-xs text-red-500 hover:text-red-700 mt-1">Clear All</button>
                      </div>
                    </div>
                  </div>

                  {/* Financial Details Section */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="text-md font-semibold mb-3">Financial Details</h4>
                    <div className="flex space-x-4">
                      <div className="flex-1">
                        <label className="text-sm font-medium mb-1 block">Premium</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R</span>
                          <input type="text" value={currencyDisplay.premium} onChange={(e) => handleCurrencyChange(e, 'premium')} className="border border-gray-300 rounded-md p-2 pl-8 w-full" placeholder="0.00" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="text-sm font-medium mb-1 block">Excess</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R</span>
                          <input type="text" value={currencyDisplay.excess} onChange={(e) => handleCurrencyChange(e, 'excess')} className="border border-gray-300 rounded-md p-2 pl-8 w-full" placeholder="0.00" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-2 mt-6">

                  <button 
  type="button" 
  onClick={() => {
    if (policyId && fromPolicy) {
      // Return to policy detail page
      const returnUrl = returnTo || `/policy/${policyId}`;
      const separator = returnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${returnUrl}${separator}refreshProducts=true&productAdded=household&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to policy detail page:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=household&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      navigate(`/create-quote/${clientId}`);
    }
  }} 
  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
>
  Cancel
</button>

                  <button type="submit" disabled={saveStatus === 'loading'} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300">
                    {saveStatus === 'loading' ? 'Saving...' : (editingId ? 'Update Contents' : 'Save Contents')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddHouseholdContentsPage;